/**
 * Sidebar.jsx
 * ─────────────────────────────────────────────────────────────────────────────
 * Right sidebar with four tabs: Indicators | Patterns | Combos | Templates.
 * Also contains the IndicatorDrawer (slide-up overlay panel).
 *
 * Architecture rules:
 *   - Only internal state: sidebarTab (which tab is active)
 *   - Adding a new tab = edit only this file
 *   - Pattern badges shown as diamonds not text labels (Fix 2)
 *   - panelContainersRef set here so useChartRenderer knows where to paint panels
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { useState } from 'react';
import { INDICATOR_REGISTRY, TEMPLATES, IND_COLORS } from '../../constants/chartConfig';

const PATTERN_REGISTRY = {
  doji:           { label: 'Doji',           color: '#94a3b8', desc: 'Indecision candle' },
  hammer:         { label: 'Hammer',         color: '#10b981', desc: 'Bullish reversal — rejected selling' },
  shooting_star:  { label: 'Shooting Star',  color: '#ef4444', desc: 'Bearish reversal — rejected buying' },
  bull_engulf:    { label: 'Bull Engulfing', color: '#10b981', desc: 'Strong buying overwhelmed selling' },
  bear_engulf:    { label: 'Bear Engulfing', color: '#ef4444', desc: 'Strong selling overwhelmed buying' },
  morning_star:   { label: 'Morning Star',   color: '#10b981', desc: '3-bar bullish reversal' },
  evening_star:   { label: 'Evening Star',   color: '#ef4444', desc: '3-bar bearish reversal' },
  marubozu_bull:  { label: 'Marubozu Bull',  color: '#10b981', desc: 'Full-body bull conviction' },
  marubozu_bear:  { label: 'Marubozu Bear',  color: '#ef4444', desc: 'Full-body bear conviction' },
};

export default function Sidebar({
  // Indicator state
  activeIndicators, onAddIndicator, onRemoveIndicator,
  // Pattern state (Fix 2)
  activePatterns, onAddPattern, onRemovePattern,
  // Combo signals display
  comboSignals,
  // Templates
  activeTemplate, onApplyTemplate,
  // Layout
  collapsed, onCollapseToggle,
  isLoggedIn,
  // Panel containers for D3 sub-panels
  panelContainersRef,
  // Analyst + alerts (shown below active lists)
  priceAlerts, onCancelAlert,
}) {
  const [sidebarTab, setSidebarTab] = useState('Indicators');

  const panelInds = activeIndicators.filter(i => INDICATOR_REGISTRY[i.key]?.type === 'panel');

  if (collapsed) {
    return (
      <div style={{ width: 20, display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 8, borderLeft: '1px solid rgba(255,255,255,0.06)' }}>
        <button onClick={onCollapseToggle} title="Expand sidebar"
          style={{ background: 'rgba(0,212,255,0.08)', border: '1px solid rgba(0,212,255,0.2)', color: '#00d4ff', borderRadius: 4, width: 18, height: 18, fontSize: 9, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          ▶
        </button>
      </div>
    );
  }

  return (
    <div style={{ width: 200, flexShrink: 0, borderLeft: '1px solid rgba(255,255,255,0.06)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

      {/* Tab header */}
      <div style={{ display: 'flex', alignItems: 'center', padding: '4px 6px', borderBottom: '1px solid rgba(255,255,255,0.06)', gap: 2 }}>
        {['Indicators','Patterns','Combos','Templates'].map(tab => (
          <button key={tab} onClick={() => setSidebarTab(tab)}
            style={{ flex: 1, padding: '2px 0', borderRadius: 3, fontFamily: 'inherit', fontSize: 9, fontWeight: sidebarTab === tab ? 700 : 400, cursor: 'pointer', border: `1px solid ${sidebarTab === tab ? 'rgba(0,212,255,0.3)' : 'transparent'}`, background: sidebarTab === tab ? 'rgba(0,212,255,0.08)' : 'transparent', color: sidebarTab === tab ? '#00d4ff' : '#6b7280' }}>
            {tab}
          </button>
        ))}
        <button onClick={onCollapseToggle} title="Collapse sidebar"
          style={{ width: 16, height: 16, borderRadius: 3, background: 'rgba(0,212,255,0.08)', border: '1px solid rgba(0,212,255,0.2)', color: '#00d4ff', fontSize: 9, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          ◀
        </button>
      </div>

      {/* Tab content */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '6px' }}>

        {/* ── Indicators tab ──────────────────────────────────────────────── */}
        {sidebarTab === 'Indicators' && (
          <>
            <div style={{ fontSize: 9, color: '#4a4a6a', fontWeight: 700, marginBottom: 4, textTransform: 'uppercase', letterSpacing: 1 }}>
              ACTIVE
            </div>
            {activeIndicators.length === 0 && (
              <div style={{ fontSize: 10, color: '#4a4a6a', marginBottom: 8 }}>No indicators active</div>
            )}
            {activeIndicators.map(ind => {
              const entry = INDICATOR_REGISTRY[ind.key];
              if (!entry) return null;
              const color = ind.params?.color || (IND_COLORS[ind.key] || ['#8b8ba7'])[0];
              const param = ind.params?.period ? ` (${ind.params.period})` : '';
              return (
                <div key={ind.instanceId} style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 3 }}>
                  <span style={{ width: 7, height: 7, borderRadius: '50%', background: color, flexShrink: 0 }} />
                  <span style={{ flex: 1, fontSize: 10, color: '#e2e8f0' }}>{entry.label}{param}</span>
                  <button onClick={() => onRemoveIndicator(ind.instanceId)}
                    style={{ background: 'none', border: 'none', color: '#4a4a6a', cursor: 'pointer', fontSize: 12, padding: 0, lineHeight: 1 }}>×</button>
                </div>
              );
            })}

            {/* Panel containers — D3 renders RSI/MACD sub-panels into these */}
            {panelInds.map(ind => (
              <div key={ind.instanceId}
                ref={el => { if (panelContainersRef) panelContainersRef.current[ind.instanceId] = el; }}
                style={{ width: '100%', marginBottom: 4, borderTop: '1px solid rgba(255,255,255,0.06)' }} />
            ))}

            {/* Alerts list (member only) */}
            {isLoggedIn && priceAlerts.length > 0 && (
              <>
                <div style={{ fontSize: 9, color: '#4a4a6a', fontWeight: 700, margin: '8px 0 4px', textTransform: 'uppercase', letterSpacing: 1 }}>ALERTS</div>
                {priceAlerts.map(a => (
                  <div key={a._id} style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 3 }}>
                    <span style={{ fontSize: 9, color: a.direction === 'above' ? '#10b981' : '#ef4444', flex: 1 }}>
                      {a.direction === 'above' ? '▲' : '▼'} ${a.targetPrice?.toFixed(2)}
                    </span>
                    <button onClick={() => onCancelAlert(a._id)}
                      style={{ background: 'none', border: 'none', color: '#4a4a6a', cursor: 'pointer', fontSize: 10, padding: 0 }}>×</button>
                  </div>
                ))}
              </>
            )}
          </>
        )}

        {/* ── Patterns tab (Fix 2 — new) ──────────────────────────────────── */}
        {sidebarTab === 'Patterns' && (
          <>
            <div style={{ fontSize: 9, color: '#4a4a6a', fontWeight: 700, marginBottom: 4, textTransform: 'uppercase', letterSpacing: 1 }}>ACTIVE</div>
            {activePatterns.length === 0 && (
              <div style={{ fontSize: 10, color: '#4a4a6a', marginBottom: 8 }}>No patterns active — add below</div>
            )}
            {activePatterns.map(p => {
              const def = PATTERN_REGISTRY[p.key];
              if (!def) return null;
              return (
                <div key={p.instanceId} style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 3 }}>
                  <span style={{ width: 8, height: 8, background: `${def.color}33`, border: `1px solid ${def.color}`, transform: 'rotate(45deg)', flexShrink: 0 }} />
                  <span style={{ flex: 1, fontSize: 10, color: '#e2e8f0' }}>{def.label}</span>
                  <button onClick={() => onRemovePattern(p.instanceId)}
                    style={{ background: 'none', border: 'none', color: '#4a4a6a', cursor: 'pointer', fontSize: 12, padding: 0 }}>×</button>
                </div>
              );
            })}

            <div style={{ fontSize: 9, color: '#4a4a6a', fontWeight: 700, margin: '8px 0 4px', textTransform: 'uppercase', letterSpacing: 1 }}>ADD PATTERN</div>
            {Object.entries(PATTERN_REGISTRY).map(([key, def]) => {
              const alreadyActive = activePatterns.some(p => p.key === key);
              return (
                <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 3 }}>
                  <span style={{ width: 8, height: 8, background: `${def.color}22`, border: `1px solid ${def.color}88`, transform: 'rotate(45deg)', flexShrink: 0 }} />
                  <span style={{ flex: 1, fontSize: 10, color: '#8b8ba7' }} title={def.desc}>{def.label}</span>
                  <button disabled={alreadyActive} onClick={() => !alreadyActive && onAddPattern(key)}
                    style={{ padding: '1px 6px', borderRadius: 3, fontFamily: 'inherit', fontSize: 10, cursor: alreadyActive ? 'default' : 'pointer', border: `1px solid ${alreadyActive ? 'rgba(255,255,255,0.06)' : 'rgba(0,212,255,0.3)'}`, background: 'transparent', color: alreadyActive ? '#4a4a6a' : '#00d4ff' }}>
                    {alreadyActive ? '✓' : '+'}
                  </button>
                </div>
              );
            })}
          </>
        )}

        {/* ── Combos tab ──────────────────────────────────────────────────── */}
        {sidebarTab === 'Combos' && (
          <>
            <div style={{ fontSize: 9, color: '#4a4a6a', fontWeight: 700, marginBottom: 4, textTransform: 'uppercase', letterSpacing: 1 }}>
              COMBINATION SIGNALS
            </div>
            {!comboSignals?.length ? (
              <div style={{ fontSize: 10, color: '#4a4a6a' }}>
                No combos detected.<br />Add patterns and indicators to enable.
              </div>
            ) : (
              comboSignals.map((sig, i) => (
                <div key={i} style={{ marginBottom: 6, padding: '5px 6px', borderRadius: 4, background: `${sig.color}0d`, border: `1px solid ${sig.color}33` }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 2 }}>
                    <span style={{ width: 8, height: 8, background: sig.color, borderRadius: 2, transform: 'rotate(45deg)', flexShrink: 0 }} />
                    <span style={{ fontSize: 10, fontWeight: 700, color: sig.color }}>{sig.desc}</span>
                    <span style={{ marginLeft: 'auto', fontSize: 8, color: sig.color, border: `1px solid ${sig.color}66`, borderRadius: 3, padding: '0 4px' }}>{sig.strength}</span>
                  </div>
                  {(sig.conditions || []).map((c, ci) => (
                    <div key={ci} style={{ fontSize: 9, color: '#8b8ba7', paddingLeft: 12 }}>✓ {c}</div>
                  ))}
                </div>
              ))
            )}
          </>
        )}

        {/* ── Templates tab ───────────────────────────────────────────────── */}
        {sidebarTab === 'Templates' && (
          <>
            <div style={{ fontSize: 9, color: '#4a4a6a', fontWeight: 700, marginBottom: 6, textTransform: 'uppercase', letterSpacing: 1 }}>TEMPLATES</div>
            {Object.entries(TEMPLATES).map(([key, tmpl]) => (
              <button key={key} onClick={() => onApplyTemplate(key)}
                style={{ display: 'block', width: '100%', textAlign: 'left', padding: '5px 8px', borderRadius: 5, fontFamily: 'inherit', fontSize: 11, fontWeight: 600, cursor: 'pointer', marginBottom: 4, border: `1px solid ${activeTemplate === key ? tmpl.color : 'rgba(255,255,255,0.08)'}`, background: activeTemplate === key ? tmpl.color + '20' : 'transparent', color: activeTemplate === key ? tmpl.color : '#6b7280', transition: 'all .15s' }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: tmpl.color, display: 'inline-block', marginRight: 6, verticalAlign: 'middle' }} />
                {tmpl.label}
              </button>
            ))}
            {!isLoggedIn && (
              <div style={{ fontSize: 9, color: '#4a4a6a', marginTop: 8 }}>Sign in to save custom layouts</div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
